import { useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import AutoCompleteTextField from "./AutoCompleteTextField";
import { getDays } from "../../utils/timeUtil";
import dayjs from "../../utils/dayjs";

type AutocompleteHighlightChangeReason =
  "keyboard" | "mouse" | "auto" | "touch";

interface OptionItem {
  value: string;
  label: string;
}

interface Props {
  type: string;
  value: string;
  options?: OptionItem[] | null;
  compareTo?: string;
  compareToValue?: string;
  handleDayChange?: ((val: OptionItem | null) => void) | null;
  handleTimeChange?: ((val: string) => void) | null;
  isOpen24Hours?: boolean;
}

const SbuxAutocomplete = ({
  type,
  value,
  options = null,
  compareTo,
  compareToValue,
  handleDayChange = null,
  handleTimeChange = null,
  isOpen24Hours = false,
}: Props) => {
  const safeOptions = options ?? [];
  const filterOption = safeOptions.find((f) => f.value === value);
  const defaultSelectedValue =
    type === "time" && !filterOption
      ? ({ value, label: value } as OptionItem)
      : (filterOption ?? null);
  const [selectedValue, setSelectedValue] = useState<OptionItem | null>(
    defaultSelectedValue,
  );
  const [inputValue, setInputValue] = useState("");

  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleHighlightChange = (
    _event: React.SyntheticEvent,
    option: OptionItem | null,
    reason: AutocompleteHighlightChangeReason,
  ) => {
    if (option && reason === "keyboard") {
      setSelectedValue(option);
    }
  };

  const handleOnChange = (
    _event: React.SyntheticEvent,
    newValue: OptionItem | string | null,
  ) => {
    if (!newValue) return;
    if (typeof newValue === "string") {
      setInputValue(newValue);
      handleTimeChange && handleTimeChange(newValue);
      return;
    }
    if (type === "time") {
      setInputValue(newValue.value);
      handleTimeChange && handleTimeChange(newValue.value);
    } else {
      setSelectedValue(newValue);
      handleDayChange && handleDayChange(newValue);
    }
  };

  const validateInput = (val: string) => {
    let isValidInput = false;
    if (typeof val === "string" && val.length > 0) {
      const days = getDays();
      const lookUpDays = days.find((f: { label: string }) => f.label === val);
      isValidInput = !!lookUpDays?.label;
    }
    isValidInput
      ? setErrorMessage("")
      : setErrorMessage("Please enter or select a valid day");
    isValidInput ? setError(false) : setError(true);
  };

  const handleOnInputChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ) => {
    if (type === "time") {
      const compareTime = dayjs(compareToValue, "hh:mm A");
      const newTime = dayjs(newValue, "hh:mm A");
      if (newTime.isValid()) {
        const isNextDay =
          (compareTo === "startTime" && newValue === "12:00 AM") ||
          (compareTo === "endTime" &&
            compareTime.format("hh:mm A") === "12:00 AM");
        const isDiff =
          compareTo === "endTime"
            ? compareTime.isSameOrAfter(newTime)
            : compareTo === "startTime" && compareTime.isSameOrBefore(newTime);

        isDiff || isNextDay
          ? setErrorMessage("")
          : setErrorMessage("Please enter a valid time");

        isDiff || isNextDay ? setError(false) : setError(true);
      } else {
        setError(true);
        setErrorMessage("Please enter a valid time");
      }
      setInputValue(newValue);
      handleTimeChange && handleTimeChange(newValue);
    } else {
      setInputValue(newValue);
      validateInput(newValue);
    }
  };

  return (
    <Autocomplete<OptionItem, false, false, true>
      size="small"
      freeSolo
      sx={{
        fontSize: 14,
        marginTop: -0.5,
        "& .MuiAutocomplete-clearIndicator": {
          display: "none",
        },
        "&.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot": {
          paddingRight: "0",
        },
      }}
      disabled={isOpen24Hours}
      defaultValue={selectedValue ?? undefined}
      options={safeOptions}
      inputValue={inputValue}
      value={selectedValue}
      onHighlightChange={handleHighlightChange}
      onChange={handleOnChange}
      onInputChange={handleOnInputChange}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.label
      }
      componentsProps={{
        popper: { style: { minWidth: 160 } },
      }}
      renderInput={(params) => (
        <AutoCompleteTextField
          params={params}
          isError={error}
          errorMessage={errorMessage}
        />
      )}
      ListboxProps={{
        style: {
          maxHeight: 150,
          fontSize: 12,
        },
      }}
    />
  );
};

export default SbuxAutocomplete;
